import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DeviceId, UtcTime } from 'shared/models';
import { State } from "reducers";
import { getCheckInsRequest } from "actions/checkInsActions";
import { getDeviceCheckIns, getDeviceCheckInsError, getDeviceCheckInsLoading } from "selectors/checkInsSelectors";

// Request incremental check-in data every thirty seconds
const RELOAD_INTERVAL = 30 * 1000;

export function useCheckIns(deviceId: DeviceId | null) {
    const reloadIntervalRef = useRef<number>();
    const checkIns = useSelector((state: State) => deviceId ? getDeviceCheckIns(state, { deviceId }) : null);
    const isLoading = useSelector((state: State) => deviceId ? getDeviceCheckInsLoading(state, { deviceId }) : false);
    const error = useSelector((state: State) => deviceId ? getDeviceCheckInsError(state, { deviceId }) : null);
    const lastTimestampRef = useRef<UtcTime | undefined>(checkIns ? checkIns[0].utcTime : undefined);

    const dispatch = useDispatch();

    //
    // Extract the latest timestamp out of the current check-in data.
    // We will use this value for requesting incremental data.
    // Note: checkIn data is stored with most recent data first.
    //
    useEffect(() => {
        lastTimestampRef.current = checkIns ? checkIns[0].utcTime : undefined;
    }, [ checkIns, lastTimestampRef ]);

    //
    // Automatically start watching for updates when we mount
    // or change deviceId's... and also automatically stop
    // watching when we unmount or change deviceId's.
    //
    useEffect(() => {
        if (!deviceId) return;

        // Send an initial request to load the latest data
        const since = lastTimestampRef.current;
        dispatch(getCheckInsRequest(deviceId, { since }));

        // Register an interval to get incremental data
        reloadIntervalRef.current = window.setInterval(() => {
            if (deviceId) {
                const since = lastTimestampRef.current;
                dispatch(getCheckInsRequest(deviceId, { since }));
            }
        }, RELOAD_INTERVAL);

        return () => {
            window.clearInterval(reloadIntervalRef.current);
        };
    }, [ deviceId, reloadIntervalRef, lastTimestampRef ]);

    return {
        checkIns,
        isLoading,
        error,
    };
}
